type LabelPalette = {
  stroke: string;
  fill: string;
};

const DEFAULT_ALPHA = 0.18;
export const AUTO_PALETTE = [
  '#e41a1c',
  '#377eb8',
  '#4daf4a',
  '#984ea3',
  '#ff7f00',
  '#ffff33',
  '#a65628',
  '#f781bf',
  '#1f78b4',
  '#33a02c',
];

const EXPLICIT_LABEL_COLORS: Record<string, string> = Object.freeze({
  RBC: '#e41a1c',
  NORMAL_RBC: '#e41a1c',
  ELLIPTOCYTES: '#377eb8',
  OVALOCYTES: '#4daf4a',
  TEARDROP_CELLS: '#984ea3',
  SCHISTOCYTES: '#ff7f00',
  SICKLE_CELLS: '#a65628',
  SPHEROCYTES: '#f781bf',
  BITE_CELLS: '#999999',
  BLISTER_CELLS: '#1b9e77',
  IRREGULARLY_CONTRACTED_CELLS: '#d95f02',
  ACANTHOCYTES: '#7570b3',
  ECHINOCYTES: '#e7298a',
  STOMATOCYTES: '#66a61e',
  TARGET_CELLS: '#a6761d',
  HYPOCHROMIC_CELLS: '#666666',
  MACROCYTES: '#fc8d62',
  OVAL_MACROCYTES: '#8da0cb',
  MICROCYTES: '#e78ac3',
  POLYCHROMASIA: '#e31a1c',
  BASOPHILIC_STIPPLING: '#1f78b4',
  HOWELL_JOLLY_BODIES: '#33a02c',
  PAPPENHEIMER_BODIES: '#6a3d9a',
  UNCLASSIFIED: '#8c564b',
  ARTIFACT: '#22d3ee',
  GHOST: '#a855f7',
  DEFAULT: '#f87171',
});

function normalizeLabel(label: string) {
  return label.trim().toUpperCase().replace(/[\s-]+/g, '_');
}

function createPalette(hex: string, alpha = DEFAULT_ALPHA): LabelPalette {
  return {
    stroke: hex,
    fill: hexToRgba(hex, alpha),
  };
}

export function hexToRgba(hex: string, alpha: number) {
  const sanitized = hex.replace('#', '');
  if (![3, 6].includes(sanitized.length)) {
    return hex;
  }
  const chunk =
    sanitized.length === 3
      ? sanitized
          .split('')
          .map((c) => c + c)
          .join('')
      : sanitized;
  const r = parseInt(chunk.slice(0, 2), 16) || 0;
  const g = parseInt(chunk.slice(2, 4), 16) || 0;
  const b = parseInt(chunk.slice(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function hashString(value: string) {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function autoPaletteColor(label: string) {
  const hash = hashString(label);
  const index = hash % AUTO_PALETTE.length;
  return AUTO_PALETTE[index] ?? AUTO_PALETTE[0];
}

export function resolveLabelPalette(
  label: string | null,
  fallbackStroke: string,
  fallbackFill: string,
): LabelPalette {
  if (!label) {
    return { stroke: fallbackStroke, fill: fallbackFill };
  }
  const normalized = normalizeLabel(label);
  const explicit = EXPLICIT_LABEL_COLORS[normalized];
  if (explicit) {
    return createPalette(explicit);
  }
  return createPalette(autoPaletteColor(normalized));
}
